import { useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, Phone, ExternalLink } from "lucide-react";
import { CTAButton } from "@/components/CTAButton";
import { Seo } from "@/components/Seo";

// N'accepte que des liens Calendly en https, puis ajoute les paramètres d'intégration
// pour afficher le calendrier directement dans la page (sans nouvelle fenêtre).
const buildCalendlyEmbed = (rawUrl) => {
  if (!rawUrl) return { embed: "", direct: "" };
  try {
    const u = new URL(rawUrl);
    const isCalendly = u.protocol === "https:" && (u.hostname === "calendly.com" || u.hostname.endsWith(".calendly.com"));
    if (!isCalendly) return { embed: "", direct: "" };
    const direct = u.toString();
    u.searchParams.set("embed_domain", window.location.hostname);
    u.searchParams.set("embed_type", "Inline");
    return { embed: u.toString(), direct };
  } catch {
    return { embed: "", direct: "" };
  }
};

export default function Merci() {
  const location = useLocation();
  const calendlyUrl = (location.state && location.state.calendlyUrl) || "";
  const { embed, direct } = useMemo(() => buildCalendlyEmbed(calendlyUrl), [calendlyUrl]);
  const withCalendar = Boolean(embed);

  return (
    <section className="bg-navy grain relative min-h-[90svh] flex items-center overflow-hidden" data-testid="merci-page">
      <Seo title="Merci pour votre demande" description="Votre demande a bien été reçue. L'équipe Umbo vous recontacte rapidement." path="/merci" />
      <div className={`relative z-10 mx-auto w-full px-5 sm:px-8 text-center ${withCalendar ? "max-w-4xl pt-32 pb-20" : "max-w-3xl py-32"}`}>
        <motion.div
          initial={{ scale: 0, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ type: "spring", stiffness: 200, damping: 15 }}
          className="mx-auto w-20 h-20 rounded-full bg-amber-600 grid place-items-center"
        >
          <Check className="w-10 h-10 text-white" />
        </motion.div>
        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.6 }}
          className="mt-8 font-display text-4xl sm:text-5xl md:text-6xl font-bold tracking-tighter text-white text-balance"
        >
          Merci, votre demande est bien reçue.
        </motion.h1>

        {withCalendar ? (
          <>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.32, duration: 0.6 }}
              className="mt-6 text-lg md:text-xl text-slate-300 leading-relaxed"
            >
              Dernière étape : choisissez dès maintenant le créneau qui vous convient pour notre échange de 30 minutes,
              sans aucun engagement. Un email de confirmation vient de vous être envoyé.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.44, duration: 0.6 }}
              className="mt-10 rounded-2xl bg-white overflow-hidden shadow-[0_30px_80px_-30px_rgba(0,0,0,0.6)]"
            >
              <iframe
                src={embed}
                title="Choisir un créneau pour votre échange avec Umbo"
                className="block w-full h-[900px] md:h-[720px] border-0"
                loading="eager"
                data-testid="merci-calendly"
              />
            </motion.div>
            <div className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-4 text-sm text-slate-300">
              <a
                href={direct}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 underline underline-offset-4 hover:text-white transition-colors"
              >
                Le calendrier ne s'affiche pas ? Ouvrez-le ici <ExternalLink className="w-3.5 h-3.5" />
              </a>
              <span className="hidden sm:inline text-slate-500">·</span>
              <a href="tel:+33320490204" className="inline-flex items-center gap-1.5 hover:text-white transition-colors">
                <Phone className="w-3.5 h-3.5" /> Ou appelez-nous au 03 20 49 02 04
              </a>
            </div>
          </>
        ) : (
          <>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.32, duration: 0.6 }}
              className="mt-6 text-lg md:text-xl text-slate-300 leading-relaxed"
            >
              Un membre de notre équipe vous recontacte très rapidement pour convenir d'un créneau,
              sans aucun engagement de votre part. Un email de confirmation vient de vous être envoyé.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.44, duration: 0.6 }}
              className="mt-10 flex flex-col sm:flex-row gap-4 justify-center"
            >
              <Link to="/"><CTAButton size="lg" data-testid="merci-home">Retour à l'accueil</CTAButton></Link>
              <a href="tel:+33320490204"><CTAButton size="lg" variant="ghostLight"><Phone className="w-4 h-4" /> 03 20 49 02 04</CTAButton></a>
            </motion.div>
          </>
        )}
      </div>
    </section>
  );
}
